/**
 * An INSTALLED thread renderer, mounted (RFC 0284 S6b).
 *
 * The second half of §4b. `resolveRenderer` has been able to FIND an app that
 * claims `handles: ['agent']` since S6; until now the shell said so and drew a
 * sentence instead of a thread, because a renderer needs to open documents and
 * move panes and none of that crossed into an installed module before S2.
 *
 * NOT A SLOT, and this file is where that is visible. It takes a `sessionId`
 * and a `projectId` from the leaf it is drawing, which is precisely what a slot
 * has none of. A sidebar is one surface per window; a thread is one per PANE,
 * and two of them side by side is the ordinary case.
 *
 * WHAT IT GETS is a container, `mod.window`, `docs`, and the SESSION it is
 * drawing. The last is the whole difference from a chrome fill: a
 * sidebar asks what exists, a thread is handed one subject and draws it.
 *
 * WHAT IT DOES NOT GET is the composer. A renderer is a VIEW of a session, and
 * submitting a turn is not a view (§4b). A bad third-party thread should look
 * like a bad app; a bad third-party composer would look like Mod is broken. So
 * the shell keeps the composer below the mount, and an installed renderer draws
 * the transcript above it.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import { projectSessionTranscript } from '@mod/core/model'
import { useEffect, useMemo, useRef } from 'react'
import { Mount } from '../../sdk/host/Mount'
import { useContribution } from '../../sdk/host/useContribution'
import type { SessionCapability, SessionView } from '../../sdk/session'
import { type SettingsSource, useAppSettings } from '../../sdk/useAppSettings'
import { useDocument } from '../../services/world/repo/useDocument'
import { ContributionWaiting } from './ContributionWaiting'

export function ThreadSlot({
  app,
  sessionId,
  projectId,
}: {
  app: SettingsSource & { id: string; name: string; viewDocId?: string }
  /** Null when the leaf is showing the session LIST rather than a session. A
   *  renderer is told so rather than being given a fake id, because "no session
   *  selected" is a state it has to draw. */
  sessionId: string | null
  projectId: string | null
}) {
  const { code, state } = useContribution(app.viewDocId)
  const [sessionDoc] = useDocument(sessionId as AnyDocumentId)
  const appSettings = useAppSettings(app)

  const view: SessionView = useMemo(
    () => ({
      id: sessionId,
      projectId,
      // The same projection the built-in thread reads, so an installed renderer
      // sees exactly what ours does rather than a lossy summary of it.
      transcript:
        sessionId && sessionDoc ? projectSessionTranscript(sessionDoc) : [],
    }),
    [sessionId, projectId, sessionDoc],
  )

  /**
   * THROUGH A REF, so the capability's identity never changes.
   *
   * A module is mounted once and keeps whatever object it was handed. If
   * `session` were a new object per render, the module would hold the FIRST one
   * forever and its `read()` would return the transcript as it was when the
   * pane opened. The ref is what makes `read()` mean "now".
   */
  const viewRef = useRef(view)
  viewRef.current = view
  const subscribers = useRef(new Set<() => void>())

  // FROM AN EFFECT, not during render. Notifying subscribers while React is
  // rendering makes a module redraw into a tree that is mid-update, and React
  // warns about exactly that.
  // `view` is the SIGNAL, not a value the body reads: the effect exists to fire
  // when the transcript changes, and dropping it would notify nobody, ever.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberate signal dependency
  useEffect(() => {
    for (const fn of subscribers.current) fn()
  }, [view])

  const session = useMemo<SessionCapability>(
    () => ({
      read: () => viewRef.current,
      onChange: (fn) => {
        subscribers.current.add(fn)
        return () => {
          subscribers.current.delete(fn)
        }
      },
    }),
    [],
  )

  if (state !== 'ready') {
    return (
      <ContributionWaiting
        name={app.name}
        what="the renderer's"
        recovery="Settings (⌘,) then Open with brings the shipped thread back."
      />
    )
  }
  // KEYED BY ID, like a slot: switching renderers boots a fresh module rather
  // than handing a mounted one a different session capability.
  return (
    <Mount
      key={app.id}
      code={code}
      title={app.name}
      settings={appSettings}
      session={session}
    />
  )
}
